// shell/components/entity-fields/form-renderers/text-input.ts

import { createElement } from '../../../utils/dom';
import type { TextFormField } from '../../entity-view/types';
import type { FormRendererResult } from './types';

/**
 * Renders a text input or textarea.
 */
export function renderTextInput<T>(
    field: TextFormField<T>,
    initialValue: unknown,
    onChange: (value: string) => void
): FormRendererResult {
    const container = createElement('div', {
        className: 'ev-field-input ev-field-input--text',
    });

    let inputEl: HTMLInputElement | HTMLTextAreaElement;

    if (field.multiline) {
        inputEl = createElement('textarea', {
            className: 'ev-input ev-textarea',
            attributes: {
                rows: String(field.rows ?? 3),
                ...(field.placeholder ? { placeholder: field.placeholder } : {}),
                ...(field.maxLength ? { maxlength: String(field.maxLength) } : {}),
                ...(field.disabled ? { disabled: '' } : {}),
                ...(field.required ? { 'aria-required': 'true' } : {}),
            },
        }) as HTMLTextAreaElement;
    } else {
        inputEl = createElement('input', {
            className: 'ev-input',
            attributes: {
                type: field.password ? 'password' : 'text',
                ...(field.placeholder ? { placeholder: field.placeholder } : {}),
                ...(field.maxLength ? { maxlength: String(field.maxLength) } : {}),
                ...(field.disabled ? { disabled: '' } : {}),
                ...(field.required ? { 'aria-required': 'true' } : {}),
            },
        }) as HTMLInputElement;
    }

    // Set initial value
    const strValue = initialValue != null ? String(initialValue) : '';
    inputEl.value = strValue;

    // Handle change
    const handleInput = () => {
        onChange(inputEl.value);
    };
    inputEl.addEventListener('input', handleInput);

    container.appendChild(inputEl);

    // Add character counter for maxLength
    let counterEl: HTMLElement | null = null;
    if (field.maxLength) {
        counterEl = createElement('div', {
            className: 'ev-char-counter',
            textContent: `${strValue.length}/${field.maxLength}`,
        });
        container.appendChild(counterEl);

        inputEl.addEventListener('input', () => {
            counterEl!.textContent = `${inputEl.value.length}/${field.maxLength}`;
        });
    }

    return {
        element: container,
        getValue: () => inputEl.value,
        setValue: (value: unknown) => {
            inputEl.value = value != null ? String(value) : '';
            if (counterEl && field.maxLength) {
                counterEl.textContent = `${inputEl.value.length}/${field.maxLength}`;
            }
        },
        validate: async () => {
            // Built-in validation handled by ValidationManager
            return true;
        },
        focus: () => inputEl.focus(),
        setDisabled: (disabled: boolean) => {
            inputEl.disabled = disabled;
        },
        dispose: () => {
            inputEl.removeEventListener('input', handleInput);
        },
    };
}
